import { useState } from 'react';
import { cadiCategoryFromMean } from '../constants/cadiDroughtLayers';
import { useTheme } from '../theme/ThemeContext';
import type { MultitemporalZonalLayer, MultitemporalZonalPanelState } from '../utils/multitemporalZonalAnalytics';
import { downloadMultitemporalZonalReportPdf } from '../utils/multitemporalZonalReportPdf';

type MultitemporalZonalPanelProps = {
  state: MultitemporalZonalPanelState;
  layer: MultitemporalZonalLayer;
  frameLabel: string;
  pestLabel?: string;
  provinces: string[];
  selectedProvince: string;
  onProvinceChange: (province: string) => void;
  onSelectMunicipality: (municipality: string | null) => void;
  onClose?: () => void;
  /** Render inside the CADI / pest layer card — no extra popup chrome. */
  embedded?: boolean;
};

function fmtNum(n: number, digits = 2): string {
  if (Number.isNaN(n)) return '—';
  return n.toFixed(digits);
}

function cadiCategory(value: number): { label: string; color: string } {
  return cadiCategoryFromMean(value);
}

function pestCategory(value: number): { label: string; color: string } {
  if (value < 0.2) return { label: 'Very low', color: '#ffffcc' };
  if (value < 0.4) return { label: 'Low', color: '#fed976' };
  if (value < 0.6) return { label: 'Moderate', color: '#fd8d3c' };
  if (value < 0.8) return { label: 'High', color: '#f03b20' };
  return { label: 'Very high', color: '#800026' };
}

function categoryFor(layer: MultitemporalZonalLayer, value: number) {
  return layer === 'cadi' ? cadiCategory(value) : pestCategory(value);
}

export default function MultitemporalZonalPanel({
  state,
  layer,
  frameLabel,
  pestLabel,
  provinces,
  selectedProvince,
  onProvinceChange,
  onSelectMunicipality,
  onClose,
  embedded = false,
}: MultitemporalZonalPanelProps) {
  const { isDark } = useTheme();
  const [pdfBusy, setPdfBusy] = useState(false);
  const shell = embedded
    ? ''
    : `rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`;
  const title = layer === 'cadi' ? 'Drought outlook' : 'Pest Occurrence Probability Index';
  const valueHeader = layer === 'cadi' ? 'Drought index' : 'Occurrence probability';

  const selectedRow =
    state.status === 'loaded' && state.selectedMunicipality
      ? state.rows.find((r) => r.municipality === state.selectedMunicipality)
      : null;

  const handleExportPdf = async () => {
    if (state.status !== 'loaded') return;
    setPdfBusy(true);
    try {
      await downloadMultitemporalZonalReportPdf({
        layer,
        province: state.province,
        frameLabel,
        pestLabel,
        rows: state.rows,
        selectedMunicipality: state.selectedMunicipality,
      });
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className={embedded ? 'mt-2 space-y-2' : `${shell} p-3.5`}>
      <div className={embedded ? 'pt-1' : 'mb-3 flex items-start justify-between gap-2'}>
        <div className="min-w-0">
          <h3 className={`font-semibold ${embedded ? 'text-xs' : 'text-sm'} ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {title}
          </h3>
          {(frameLabel || pestLabel) && (
            <p className={`truncate ${embedded ? 'text-[11px]' : 'text-xs'} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {frameLabel}
              {pestLabel ? ` · ${pestLabel}` : ''}
            </p>
          )}
        </div>
        {!embedded && onClose && (
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-black/5" aria-label="Close">
            <i className="ri-close-line text-lg" />
          </button>
        )}
      </div>

      <div>
        <label
          htmlFor={`multitemporal-province-${layer}`}
          className={`mb-1 block text-[11px] font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
        >
          Province
        </label>
        <select
          id={`multitemporal-province-${layer}`}
          value={selectedProvince}
          disabled={provinces.length === 0}
          onChange={(e) => onProvinceChange(e.target.value)}
          className={`w-full rounded-lg border px-2 py-1.5 text-sm ${
            isDark
              ? 'border-[#2d3748] bg-[#1c2432] text-[#e6edf3]'
              : 'border-[#e5e7eb] bg-white text-[#111827]'
          }`}
        >
          <option value="">
            {provinces.length === 0 ? 'Loading provinces…' : 'Select province...'}
          </option>
          {provinces.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {!selectedProvince && (
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Choose a province to see municipality summaries.
        </p>
      )}

      {selectedProvince && state.status === 'loading' && (
        <div className="mt-2">
          <div className="mb-1 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{
                width: `${state.total > 0 ? (state.progress / state.total) * 100 : 30}%`,
              }}
            />
          </div>
          <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading municipalities… {state.progress}/{state.total}
          </p>
        </div>
      )}

      {state.status === 'error' && (
        <p className="mt-2 text-xs text-red-500">{state.message}</p>
      )}

      {state.status === 'loaded' && (
        <>
          {selectedRow?.stats && (
            <div
              className={`mb-3 rounded-lg border px-3 py-2 ${
                isDark ? 'border-amber-800/40 bg-amber-950/20' : 'border-amber-100 bg-amber-50/80'
              }`}
            >
              <p className={`text-xs font-semibold ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
                {state.selectedMunicipality}
              </p>
              <p className={`mt-1 text-lg font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {fmtNum(selectedRow.stats.mean)}
                <span className={`ml-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {categoryFor(layer, selectedRow.stats.mean).label}
                </span>
              </p>
              <p className={`text-[10px] tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Min {fmtNum(selectedRow.stats.min)} · Max {fmtNum(selectedRow.stats.max)}
              </p>
            </div>
          )}

          <div className={`${embedded ? 'max-h-48' : 'max-h-[min(50vh,320px)]'} overflow-y-auto`}>
            <table className="w-full border-collapse text-[11px]">
              <thead className={`sticky top-0 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                <tr className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                  <th className="pb-1.5 text-left font-medium">Municipality</th>
                  <th className="pb-1.5 text-right font-medium">{valueHeader}</th>
                  <th className="pb-1.5 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {state.rows.map((row) => {
                  const isSelected = state.selectedMunicipality === row.municipality;
                  const cat = row.stats ? categoryFor(layer, row.stats.mean) : null;
                  return (
                    <tr
                      key={row.municipality}
                      onClick={() => onSelectMunicipality(isSelected ? null : row.municipality)}
                      className={`cursor-pointer border-t transition-colors ${
                        isDark ? 'border-gray-700' : 'border-gray-100'
                      } ${isSelected ? (isDark ? 'bg-emerald-900/30' : 'bg-emerald-50') : ''} ${
                        isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className={`py-1.5 pr-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {row.municipality}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {row.stats ? fmtNum(row.stats.mean) : '—'}
                      </td>
                      <td className="py-1.5 text-right">
                        {cat ? (
                          <span
                            className="inline-block rounded px-1.5 py-0.5 text-[9px] font-medium"
                            style={{
                              backgroundColor: `${cat.color}33`,
                              color: isDark ? '#f3f4f6' : '#1f2937',
                            }}
                          >
                            {cat.label}
                          </span>
                        ) : (
                          <span className="text-[9px] text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {state.rows.filter((r) => r.stats).length} of {state.rows.length} municipalities with
              data
            </p>
            <button
              type="button"
              disabled={pdfBusy}
              onClick={handleExportPdf}
              className={`inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white disabled:opacity-60 ${
                isDark ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-[#008540] hover:opacity-90'
              }`}
            >
              <i className="ri-file-pdf-2-line" aria-hidden />
              {pdfBusy ? 'Preparing…' : 'Export PDF'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
